import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./UplineDownlinePage.css";

const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const LEVELS = ["1", "2", "3", "4", "5"];
const ACCOUNT_STATUS_OPTIONS = ["Active", "Inactive", "Blocked"];
const COLORS = {
  staking: "#0f766e",
  brokerage: "#2563eb",
  referral: "#d97706",
  signup: "#7c3aed",
  income: "#0f766e",
  distributed: "#c2410c",
  level: ["#0f766e", "#0d9488", "#14b8a6", "#5eead4", "#99f6e4"],
};

const KYC_LABELS = {
  0: "Not submitted",
  1: "Pending",
  2: "Verified",
  3: "Rejected",
};
const KYC_CLASS = { 0: "muted", 1: "pending", 2: "ok", 3: "bad" };

const asNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatAmount = (v) => {
  const n = asNum(v);
  if (Math.abs(n) >= 1000) return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  return n.toLocaleString("en-IN", { maximumFractionDigits: 8 });
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const userDisplayName = (user) => {
  const parts = [user?.firstName, user?.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const formatMobile = (user) => {
  if (!user?.mobileNumber) return "—";
  return `${user.country_code || ""} ${user.mobileNumber}`.trim();
};

const kycLabel = (v) => KYC_LABELS[Number(v)] ?? (v == null || v === "" ? "—" : String(v));
const kycClass = (v) => KYC_CLASS[Number(v)] || "muted";
const accountClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "ok";
  if (s === "blocked") return "bad";
  return "muted";
};

const extraRecipientUser = (item) => item?.user || item?.recipient || item;
const extraRecipientCommission = (item) =>
  item?.commission_received_from_this_user || item?.commission || item?.income || null;

const DonutChart = ({ slices, size = 168, thickness = 22, centerLabel, centerSub }) => {
  const valid = (slices || []).filter((s) => asNum(s.value) > 0);
  const total = valid.reduce((sum, s) => sum + asNum(s.value), 0);
  const radius = (size - thickness) / 2;
  const circ = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="ud-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={thickness}
        />
        {total > 0 &&
          valid.map((s) => {
            const len = (asNum(s.value) / total) * circ;
            const el = (
              <circle
                key={s.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={s.color}
                strokeWidth={thickness}
                strokeDasharray={`${len} ${circ - len}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += len;
            return el;
          })}
      </svg>
      <div className="ud-donut__center">
        <strong>{centerLabel}</strong>
        {centerSub ? <span>{centerSub}</span> : null}
      </div>
    </div>
  );
};

const BarList = ({ items }) => {
  const max = Math.max(...(items || []).map((i) => asNum(i.value)), 0) || 1;
  return (
    <div className="ud-bars">
      {(items || []).map((item) => (
        <div key={item.label} className="ud-bar-row">
          <span className="ud-bar-row__label" title={item.label}>
            {item.label}
          </span>
          <div className="ud-bar-row__track">
            <div
              className="ud-bar-row__fill"
              style={{
                width: `${(asNum(item.value) / max) * 100}%`,
                background: item.color || COLORS.income,
              }}
            />
          </div>
          <span className="ud-bar-row__value">{formatAmount(item.value)}</span>
        </div>
      ))}
    </div>
  );
};

const LevelChart = ({ byLevel, color = COLORS.income }) => {
  const items = LEVELS.map((lvl, idx) => ({
    label: `L${lvl}`,
    value: asNum(byLevel?.[lvl]),
    color: COLORS.level[idx] || color,
  }));
  const hasData = items.some((i) => i.value > 0);
  if (!hasData) return <p className="ud-muted">No level-wise amount yet.</p>;
  return <BarList items={items} />;
};

const ChartLegend = ({ items }) => (
  <ul className="ud-legend">
    {items.map((item) => (
      <li key={item.label}>
        <span style={{ background: item.color }} />
        {item.label}
        <strong>{formatAmount(item.value)}</strong>
      </li>
    ))}
  </ul>
);

const Chip = ({ kind, children }) => (
  <span className={`ud-chip ud-chip--${kind}`}>{children}</span>
);

const UserMini = ({ user, empty = "—" }) => {
  if (!user || (!user._id && !user.emailId && !user.uuid)) return empty;
  return (
    <div className="ud-user-mini">
      <strong>{userDisplayName(user)}</strong>
      <span>{user.emailId || user.uuid || "—"}</span>
    </div>
  );
};

const UplineDownlinePage = () => {
  const [mode, setMode] = useState("downline");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [accountStatus, setAccountStatus] = useState("");
  const [kycStatus, setKycStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportToast, setExportToast] = useState(null);

  const buildParams = useCallback(
    (page, limit, searchOverride) => {
      const q = typeof searchOverride === "string" ? searchOverride : search;
      const params = { page, limit };
      if (q.trim()) params.search = q.trim();
      if (accountStatus) params.account_status = accountStatus;
      if (kycStatus !== "") params.kyc_status = Number(kycStatus);
      return params;
    },
    [search, accountStatus, kycStatus]
  );

  const fetchRows = useCallback(
    async (page = 1, limit = pagination.limit, searchOverride) => {
      setLoading(true);
      setError("");
      try {
        const res = await AdminService.getReferralUsersCommissionOverview(
          buildParams(page, limit, searchOverride)
        );
        if (res?.success) {
          setRows(Array.isArray(res?.data) ? res.data : []);
          setPagination((prev) => ({
            ...prev,
            ...(res?.pagination || {}),
            page: res?.pagination?.page || page,
            limit: res?.pagination?.limit ?? limit,
          }));
        } else {
          setRows([]);
          setError(res?.message || "Failed to fetch upline / downline data.");
        }
      } catch (err) {
        setRows([]);
        setError(err?.response?.data?.message || err?.message || "Failed to fetch upline / downline data.");
      } finally {
        setLoading(false);
      }
    },
    [buildParams, pagination.limit]
  );

  useEffect(() => {
    fetchRows(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load
  }, []);

  const applyFilters = (nextAccount, nextKyc) => {
    setAccountStatus(nextAccount);
    setKycStatus(nextKyc);
    setSelected(null);
    const params = { page: 1, limit: pagination.limit };
    if (search.trim()) params.search = search.trim();
    if (nextAccount) params.account_status = nextAccount;
    if (nextKyc !== "") params.kyc_status = Number(nextKyc);
    setLoading(true);
    setError("");
    AdminService.getReferralUsersCommissionOverview(params)
      .then((res) => {
        if (res?.success) {
          setRows(Array.isArray(res?.data) ? res.data : []);
          setPagination((prev) => ({
            ...prev,
            ...(res?.pagination || {}),
            page: res?.pagination?.page || 1,
            limit: res?.pagination?.limit ?? pagination.limit,
          }));
        } else {
          setRows([]);
          setError(res?.message || "Failed to fetch upline / downline data.");
        }
      })
      .catch((err) => {
        setRows([]);
        setError(err?.response?.data?.message || err?.message || "Failed to fetch upline / downline data.");
      })
      .finally(() => setLoading(false));
  };

  const charts = useMemo(() => {
    const downlineByLevel = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const uplineByLevel = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const totals = {
      staking: 0,
      brokerage: 0,
      referral: 0,
      signup: 0,
      income: 0,
      distStaking: 0,
      distBrokerage: 0,
      distReferral: 0,
      distributed: 0,
    };
    const topDownline = [];
    const topUpline = [];

    rows.forEach((row) => {
      const income = row?.income || {};
      const dist = row?.distributed_to_uplines || {};
      totals.staking += asNum(income.staking?.total);
      totals.brokerage += asNum(income.brokerage?.total);
      totals.referral += asNum(income.referral_bonus?.total);
      totals.signup += asNum(income.signup_bonus?.total);
      totals.income += asNum(income.combined_total);
      totals.distStaking += asNum(dist.staking?.total);
      totals.distBrokerage += asNum(dist.brokerage?.total);
      totals.distReferral += asNum(dist.referral_bonus?.total);
      totals.distributed += asNum(dist.combined_total);

      LEVELS.forEach((lvl) => {
        downlineByLevel[lvl] +=
          asNum(income.staking?.by_level?.[lvl]?.total) +
          asNum(income.brokerage?.by_level?.[lvl]?.total) +
          asNum(income.referral_bonus?.by_level?.[lvl]?.total);
      });
      (Array.isArray(row?.uplines) ? row.uplines : []).forEach((ul) => {
        const lvl = String(ul.level || "");
        if (uplineByLevel[lvl] != null) {
          uplineByLevel[lvl] += asNum(ul.commission_received_from_this_user?.combined_total);
        }
      });

      const name = userDisplayName(row?.user);
      topDownline.push({ label: name, value: asNum(income.combined_total), color: COLORS.income });
      topUpline.push({
        label: name,
        value: asNum(dist.combined_total),
        color: COLORS.distributed,
      });
    });

    topDownline.sort((a, b) => b.value - a.value);
    topUpline.sort((a, b) => b.value - a.value);

    return {
      totals,
      downlineByLevel,
      uplineByLevel,
      topDownline: topDownline.slice(0, 6),
      topUpline: topUpline.slice(0, 6),
      downlineSlices: [
        { label: "Staking", value: totals.staking, color: COLORS.staking },
        { label: "Brokerage", value: totals.brokerage, color: COLORS.brokerage },
        { label: "Referral bonus", value: totals.referral, color: COLORS.referral },
        { label: "Signup bonus", value: totals.signup, color: COLORS.signup },
      ],
      uplineSlices: [
        { label: "Staking", value: totals.distStaking, color: COLORS.staking },
        { label: "Brokerage", value: totals.distBrokerage, color: COLORS.brokerage },
        { label: "Referral bonus", value: totals.distReferral, color: COLORS.referral },
      ],
    };
  }, [rows]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchDraft.trim();
    setSearch(q);
    setSelected(null);
    fetchRows(1, pagination.limit, q);
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    setExportToast(null);
    try {
      let currentPage = 1;
      let lastPage = 1;
      const all = [];
      do {
        const res = await AdminService.getReferralUsersCommissionOverview(buildParams(currentPage, 100));
        if (res?.success === false && !Array.isArray(res?.data)) {
          throw new Error(res?.message || "Failed to export.");
        }
        all.push(...(Array.isArray(res?.data) ? res.data : []));
        lastPage = res?.pagination?.total_pages || 1;
        currentPage += 1;
      } while (currentPage <= lastPage);

      if (!all.length) {
        setExportToast({ type: "error", message: "No data to export." });
        setTimeout(() => setExportToast(null), 3000);
        return;
      }

      const exportData = all.map((row) => {
        const u = row?.user || {};
        const sponsor = row?.direct_sponsor || {};
        const income = row?.income || {};
        const dist = row?.distributed_to_uplines || {};
        return {
          Name: userDisplayName(u) === "—" ? "" : userDisplayName(u),
          Email: u.emailId ?? "",
          UUID: u.uuid ?? "",
          Mobile: formatMobile(u) === "—" ? "" : formatMobile(u),
          "Referral Code": u.referral_code ?? "",
          KYC: kycLabel(u.kycVerified),
          "Account Status": u.account_status ?? "",
          "Direct Sponsor": userDisplayName(sponsor) === "—" ? sponsor.emailId ?? "" : userDisplayName(sponsor),
          "Downline Combined": income.combined_total ?? 0,
          "Downline Staking": income.staking?.total ?? 0,
          "Downline Brokerage": income.brokerage?.total ?? 0,
          "Downline Referral Bonus": income.referral_bonus?.total ?? 0,
          "Downline Signup Bonus": income.signup_bonus?.total ?? 0,
          "Upline Combined": dist.combined_total ?? 0,
          "Upline Staking": dist.staking?.total ?? 0,
          "Upline Brokerage": dist.brokerage?.total ?? 0,
          "Upline Referral Bonus": dist.referral_bonus?.total ?? 0,
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Upline Downline");
      XLSX.writeFile(workbook, `upline_downline_${new Date().toISOString().slice(0, 10)}.xlsx`);
      setExportToast({ type: "success", message: `Exported ${all.length} row(s) to Excel.` });
      setTimeout(() => setExportToast(null), 4000);
    } catch (err) {
      setExportToast({ type: "error", message: err?.message || "Failed to export Excel file." });
      setTimeout(() => setExportToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  const page = pagination.page || 1;
  const limit = pagination.limit || 20;
  const total = pagination.total ?? 0;
  const totalPages = pagination.total_pages || 1;
  const pageStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const pageEnd = total === 0 ? 0 : Math.min(page * limit, total);
  const isDownline = mode === "downline";

  const renderDetail = (row) => {
    const user = row?.user || {};
    const income = row?.income || {};
    const distributed = row?.distributed_to_uplines || {};
    const uplines = Array.isArray(row?.uplines) ? [...row.uplines].sort((a, b) => asNum(a.level) - asNum(b.level)) : [];
    const extras = Array.isArray(row?.extra_commission_recipients) ? row.extra_commission_recipients : [];
    const incomeSlices = [
      { label: "Staking", value: asNum(income.staking?.total), color: COLORS.staking },
      { label: "Brokerage", value: asNum(income.brokerage?.total), color: COLORS.brokerage },
      { label: "Referral bonus", value: asNum(income.referral_bonus?.total), color: COLORS.referral },
      { label: "Signup bonus", value: asNum(income.signup_bonus?.total), color: COLORS.signup },
    ];
    const distSlices = [
      { label: "Staking", value: asNum(distributed.staking?.total), color: COLORS.staking },
      { label: "Brokerage", value: asNum(distributed.brokerage?.total), color: COLORS.brokerage },
      { label: "Referral bonus", value: asNum(distributed.referral_bonus?.total), color: COLORS.referral },
    ];
    const uplineBars = uplines.map((ul, idx) => ({
      label: `L${ul.level} ${userDisplayName(ul.user)}`,
      value: asNum(ul.commission_received_from_this_user?.combined_total),
      color: COLORS.level[idx] || COLORS.distributed,
    }));
    const downlineLevel = {};
    LEVELS.forEach((lvl) => {
      downlineLevel[lvl] =
        asNum(income.staking?.by_level?.[lvl]?.total) +
        asNum(income.brokerage?.by_level?.[lvl]?.total) +
        asNum(income.referral_bonus?.by_level?.[lvl]?.total);
    });
    const uplineLevel = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    uplines.forEach((ul) => {
      const lvl = String(ul.level || "");
      if (uplineLevel[lvl] != null) {
        uplineLevel[lvl] += asNum(ul.commission_received_from_this_user?.combined_total);
      }
    });

    return (
      <section className="ud-card">
        <div className="ud-detail-head">
          <button type="button" className="ud-btn-back" onClick={() => setSelected(null)}>
            <i className="fas fa-arrow-left" /> Back to {isDownline ? "downline" : "upline"} list
          </button>
          <div>
            <h2>{userDisplayName(user)}</h2>
            <p>
              {user.emailId || "—"} · {user.uuid || "—"} · {formatMobile(user)}
            </p>
          </div>
        </div>

        <div className="ud-detail-meta">
          <span>
            Referral <strong>{user.referral_code || "—"}</strong>
          </span>
          <Chip kind={kycClass(user.kycVerified)}>{kycLabel(user.kycVerified)}</Chip>
          <Chip kind={accountClass(user.account_status)}>{user.account_status || "—"}</Chip>
          <span>
            Joined <strong>{formatDate(user.createdAt)}</strong>
          </span>
          <span>
            Total refer <strong>{user.total_refer ?? 0}</strong>
          </span>
        </div>

        {isDownline ? (
          <>
            <p className="ud-help">
              Downline view — commission this user earned from people below them.
            </p>
            <div className="ud-charts">
              <article className="ud-chart-card">
                <h3>Income sources</h3>
                <div className="ud-chart-card__body">
                  <DonutChart
                    slices={incomeSlices}
                    centerLabel={formatAmount(income.combined_total)}
                    centerSub="Total earned"
                  />
                  <ChartLegend items={incomeSlices} />
                </div>
              </article>
              <article className="ud-chart-card">
                <h3>Earned by downline level</h3>
                <LevelChart byLevel={downlineLevel} />
              </article>
            </div>
            <div className="ud-stat-grid">
              <div>
                <span>Staking</span>
                <strong>{formatAmount(income.staking?.total)}</strong>
                <small>{income.staking?.count ?? 0} txns</small>
              </div>
              <div>
                <span>Brokerage</span>
                <strong>{formatAmount(income.brokerage?.total)}</strong>
                <small>{income.brokerage?.count ?? 0} txns</small>
              </div>
              <div>
                <span>Referral bonus</span>
                <strong>{formatAmount(income.referral_bonus?.total)}</strong>
                <small>{income.referral_bonus?.count ?? 0} txns</small>
              </div>
              <div>
                <span>Signup bonus</span>
                <strong>{formatAmount(income.signup_bonus?.total)}</strong>
                <small>{income.signup_bonus?.count ?? 0} txns</small>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="ud-help">
              Upline view — commission this user generated for sponsors above them.
            </p>
            <div className="ud-charts">
              <article className="ud-chart-card">
                <h3>Paid to uplines by source</h3>
                <div className="ud-chart-card__body">
                  <DonutChart
                    slices={distSlices}
                    centerLabel={formatAmount(distributed.combined_total)}
                    centerSub="Sent upward"
                  />
                  <ChartLegend items={distSlices} />
                </div>
              </article>
              <article className="ud-chart-card">
                <h3>Paid by upline level</h3>
                <LevelChart byLevel={uplineLevel} color={COLORS.distributed} />
              </article>
            </div>

            <h3 className="ud-block-title">Sponsor chain</h3>
            <div className="ud-chain">
              <div className="ud-chain-node ud-chain-node--self">
                <span>User</span>
                <strong>{userDisplayName(user)}</strong>
              </div>
              {uplines.length === 0 ? (
                <p className="ud-muted">No uplines in the 5-level chain.</p>
              ) : (
                uplines.map((ul) => (
                  <React.Fragment key={ul.user?._id || ul.level}>
                    <i className="fas fa-arrow-right ud-chain-arrow" />
                    <div className="ud-chain-node">
                      <span>L{ul.level}</span>
                      <strong>{userDisplayName(ul.user)}</strong>
                      <em>{formatAmount(ul.commission_received_from_this_user?.combined_total)}</em>
                      <small>{ul.user?.emailId || ul.user?.uuid || "—"}</small>
                    </div>
                  </React.Fragment>
                ))
              )}
            </div>

            {uplineBars.length > 0 ? (
              <article className="ud-chart-card" style={{ marginTop: "1rem" }}>
                <h3>Each upline received</h3>
                <BarList items={uplineBars} />
              </article>
            ) : null}

            <div className="ud-upline-list">
              {uplines.map((ul) => {
                const comm = ul.commission_received_from_this_user || {};
                return (
                  <div key={ul.user?._id || ul.level} className="ud-upline-card">
                    <div className="ud-upline-card__head">
                      <Chip kind="level">L{ul.level}</Chip>
                      <UserMini user={ul.user} />
                      <strong className="ud-upline-card__total">{formatAmount(comm.combined_total)}</strong>
                    </div>
                    <div className="ud-upline-split">
                      <span>Staking {formatAmount(comm.staking?.total)}</span>
                      <span>Brokerage {formatAmount(comm.brokerage?.total)}</span>
                      <span>Referral {formatAmount(comm.referral_bonus?.total)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <h3 className="ud-block-title">Outside 5-level chain</h3>
            {extras.length === 0 ? (
              <p className="ud-muted">No extra commission recipients.</p>
            ) : (
              extras.map((item, idx) => {
                const extraUser = extraRecipientUser(item);
                const extraComm = extraRecipientCommission(item);
                return (
                  <div key={extraUser?._id || idx} className="ud-upline-card">
                    <div className="ud-upline-card__head">
                      <Chip kind="muted">Extra</Chip>
                      <UserMini user={extraUser} />
                      {extraComm ? (
                        <strong className="ud-upline-card__total">
                          {formatAmount(extraComm.combined_total)}
                        </strong>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </section>
    );
  };

  return (
    <main className="dashboard-main dashboard-subpage ud-page">
      <section className="dashboard-hero ud-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Upline &amp; Downline</span>
          </p>
          <h1 className="dashboard-hero__title">Upline &amp; Downline</h1>
          <p className="dashboard-hero__subtitle">
            Downline list = commission a user earned from their network. Upline list = commission that
            user&apos;s activity paid to sponsors.
          </p>
        </div>
        <div className="ud-hero__icon">
          <i className="fas fa-project-diagram" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="ud-mode-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={isDownline}
            className={`ud-mode-tab ${isDownline ? "ud-mode-tab--active" : ""}`}
            onClick={() => {
              setMode("downline");
              setSelected(null);
            }}
          >
            <i className="fas fa-level-down-alt" />
            Downline income
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={!isDownline}
            className={`ud-mode-tab ${!isDownline ? "ud-mode-tab--active" : ""}`}
            onClick={() => {
              setMode("upline");
              setSelected(null);
            }}
          >
            <i className="fas fa-level-up-alt" />
            Upline payouts
          </button>
        </div>

        {selected ? (
          renderDetail(selected)
        ) : (
          <>
            <div className="ud-stats">
              <div className="ud-stat">
                <span>{isDownline ? "Downline earned (this page)" : "Paid to uplines (this page)"}</span>
                <strong>
                  {formatAmount(isDownline ? charts.totals.income : charts.totals.distributed)}
                </strong>
              </div>
              <div className="ud-stat">
                <span>Users</span>
                <strong>{total}</strong>
              </div>
              <div className="ud-stat">
                <span>{isDownline ? "Staking + brokerage" : "Direct sponsors on page"}</span>
                <strong>
                  {isDownline
                    ? formatAmount(charts.totals.staking + charts.totals.brokerage)
                    : rows.filter((r) => r?.direct_sponsor).length}
                </strong>
              </div>
            </div>

            <div className="ud-charts">
              <article className="ud-chart-card">
                <h3>{isDownline ? "Where downline income came from" : "What was paid upward"}</h3>
                <div className="ud-chart-card__body">
                  <DonutChart
                    slices={isDownline ? charts.downlineSlices : charts.uplineSlices}
                    centerLabel={formatAmount(isDownline ? charts.totals.income : charts.totals.distributed)}
                    centerSub="This page"
                  />
                  <ChartLegend items={isDownline ? charts.downlineSlices : charts.uplineSlices} />
                </div>
              </article>
              <article className="ud-chart-card">
                <h3>{isDownline ? "Income by downline level" : "Payout by upline level"}</h3>
                <LevelChart
                  byLevel={isDownline ? charts.downlineByLevel : charts.uplineByLevel}
                  color={isDownline ? COLORS.income : COLORS.distributed}
                />
              </article>
              <article className="ud-chart-card">
                <h3>{isDownline ? "Top earners on this page" : "Top users sending to uplines"}</h3>
                {(isDownline ? charts.topDownline : charts.topUpline).some((i) => i.value > 0) ? (
                  <BarList items={isDownline ? charts.topDownline : charts.topUpline} />
                ) : (
                  <p className="ud-muted">No amounts on this page yet.</p>
                )}
              </article>
            </div>

            <section className="ud-card">
              <div className="ud-toolbar">
                <form onSubmit={handleSearchSubmit}>
                  <input
                    type="search"
                    placeholder="Search email, UUID, mobile, name, referral code…"
                    value={searchDraft}
                    onChange={(e) => setSearchDraft(e.target.value)}
                    aria-label="Search users"
                  />
                  <button type="submit">Search</button>
                </form>
                <label>
                  Account
                  <select
                    value={accountStatus}
                    disabled={loading}
                    onChange={(e) => applyFilters(e.target.value, kycStatus)}
                  >
                    <option value="">All</option>
                    {ACCOUNT_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  KYC
                  <select
                    value={kycStatus}
                    disabled={loading}
                    onChange={(e) => applyFilters(accountStatus, e.target.value)}
                  >
                    <option value="">All</option>
                    {Object.entries(KYC_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="ud-export"
                  onClick={handleExportExcel}
                  disabled={exportLoading}
                >
                  <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                  {exportLoading ? "Exporting…" : "Excel"}
                </button>
              </div>

              {exportToast ? (
                <p className={`ud-toast ud-toast--${exportToast.type}`}>{exportToast.message}</p>
              ) : null}

              {loading ? (
                <div className="ud-empty">
                  <i className="fas fa-spinner fa-spin" />
                  <p>Loading {isDownline ? "downline" : "upline"} data…</p>
                </div>
              ) : error ? (
                <div className="ud-empty">
                  <i className="fas fa-exclamation-circle" />
                  <p>{error}</p>
                </div>
              ) : rows.length === 0 ? (
                <div className="ud-empty">
                  <i className="fas fa-project-diagram" />
                  <p>No users match this filter</p>
                </div>
              ) : (
                <>
                  <div className="ud-table-wrap">
                    <table className="ud-table">
                      <thead>
                        {isDownline ? (
                          <tr>
                            <th>User</th>
                            <th>UUID</th>
                            <th>KYC</th>
                            <th>Account</th>
                            <th>Staking</th>
                            <th>Brokerage</th>
                            <th>Referral</th>
                            <th>Signup</th>
                            <th>Total earned</th>
                            <th>Action</th>
                          </tr>
                        ) : (
                          <tr>
                            <th>User</th>
                            <th>UUID</th>
                            <th>Direct sponsor</th>
                            <th>Uplines</th>
                            <th>Staking sent</th>
                            <th>Brokerage sent</th>
                            <th>Referral sent</th>
                            <th>Total to uplines</th>
                            <th>Action</th>
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {rows.map((row) => {
                          const u = row?.user || {};
                          const income = row?.income || {};
                          const dist = row?.distributed_to_uplines || {};
                          const uplineCount = Array.isArray(row?.uplines) ? row.uplines.length : 0;
                          return (
                            <tr key={u._id || u.uuid}>
                              <td>
                                <UserMini user={u} />
                              </td>
                              <td>{u.uuid || "—"}</td>
                              {isDownline ? (
                                <>
                                  <td>
                                    <Chip kind={kycClass(u.kycVerified)}>{kycLabel(u.kycVerified)}</Chip>
                                  </td>
                                  <td>
                                    <Chip kind={accountClass(u.account_status)}>
                                      {u.account_status || "—"}
                                    </Chip>
                                  </td>
                                  <td>{formatAmount(income.staking?.total)}</td>
                                  <td>{formatAmount(income.brokerage?.total)}</td>
                                  <td>{formatAmount(income.referral_bonus?.total)}</td>
                                  <td>{formatAmount(income.signup_bonus?.total)}</td>
                                  <td>
                                    <strong>{formatAmount(income.combined_total)}</strong>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td>
                                    <UserMini user={row.direct_sponsor} empty="No sponsor" />
                                  </td>
                                  <td>{uplineCount}</td>
                                  <td>{formatAmount(dist.staking?.total)}</td>
                                  <td>{formatAmount(dist.brokerage?.total)}</td>
                                  <td>{formatAmount(dist.referral_bonus?.total)}</td>
                                  <td>
                                    <strong>{formatAmount(dist.combined_total)}</strong>
                                  </td>
                                </>
                              )}
                              <td>
                                <button type="button" className="ud-btn-view" onClick={() => setSelected(row)}>
                                  View
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="ud-pagination">
                    <label>
                      Rows per page
                      <select
                        value={limit}
                        disabled={loading}
                        onChange={(e) => {
                          const next = Number(e.target.value) || 20;
                          setPagination((prev) => ({ ...prev, limit: next, page: 1 }));
                          fetchRows(1, next, search);
                        }}
                      >
                        {PAGE_LIMIT_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                    <span>
                      {total > 0
                        ? `Showing ${pageStart}–${pageEnd} of ${total} · Page ${page} of ${totalPages}`
                        : `Page ${page} of ${totalPages}`}
                    </span>
                    <div>
                      <button
                        type="button"
                        disabled={page <= 1 || loading}
                        onClick={() => fetchRows(page - 1, limit, search)}
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        disabled={page >= totalPages || loading}
                        onClick={() => fetchRows(page + 1, limit, search)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default UplineDownlinePage;
